import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { RequestSaveSetting, Setting, SettingsApi } from 'oas/userconfig';
import { rethrowSwallowedError, useApiMutation } from '../use-api-mutation';
import { API_NAMES } from '../constants';

export interface SaveUserConfigSettingVariables {
    settingKey: string;
    value: RequestSaveSetting['value'];
}

export const useSaveUserConfigSetting = (): UseMutationResult<Setting, unknown, SaveUserConfigSettingVariables> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async ({ settingKey, value }, apiConfig, axiosInstance) => {
            const response = rethrowSwallowedError(
                await new SettingsApi(apiConfig, '', axiosInstance).putSetting(settingKey, { value }),
            );

            return response.data;
        },
        {
            // invalidate ONLY ON SUCCESS  because invalidating after a failure refetches the query that just failed,
            // which loops the page when the service is unavailable
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['user-config'] });
            },
        },
        [],
        'v6',
        API_NAMES.USER_CONFIG,
    );
};

export const useDeleteUserConfigSetting = (): UseMutationResult<void, unknown, string> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (settingKey, apiConfig, axiosInstance) => {
            rethrowSwallowedError(await new SettingsApi(apiConfig, '', axiosInstance).deleteSetting(settingKey));
        },
        {
            // same as above
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['user-config'] });
            },
        },
        [],
        'v6',
        API_NAMES.USER_CONFIG,
    );
};
